import { useEffect, useState, type RefObject } from "react";
import { useAppContext } from "@/Components/Providers/AppProvider";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faXmark } from "@fortawesome/free-solid-svg-icons";
import LockBodyDummy from '@/Components/LockBodyDummy';
import { useEventListener } from 'usehooks-ts';
import { SOCIAL_LINKS, CONTACT_DETAILS } from "@/Lib/siteData";
import { useMediaQuery } from "@uidotdev/usehooks";

type Props = {};

export default function NavigationHeader({ }: Props) {
    const isDesktop = useMediaQuery('(min-width: 768px)');

    const {
        mobileNavigationMenu: [mobileNavigationOpen, setMobileNavigationOpen],
        miniContactMenu: [contactMenuOpen, setContactMenuOpen],
        navigationBarAnchor,
        homepageAnchors
    } = useAppContext();

    const [mobileNavigationHeight, setMobileNavigationHeight] = useState(0);

    const NAVIGATION_ITEMS = [
        {
            text: 'About',
            id: 'about',
            ref: homepageAnchors.about,
            offset: 100,
            delay: 'delay-50',
        },
        {
            text: 'Services',
            id: 'services',
            ref: homepageAnchors.services,
            offset: 30,
            delay: 'delay-100',
        },
        {
            text: 'Testimonials',
            id: 'testimonials',
            ref: homepageAnchors.testimonials,
            offset: -60,
            delay: 'delay-150',
        },
    ];

    const handleMobileMenuClick = () => {
        setMobileNavigationOpen(!mobileNavigationOpen);
        if (navigationBarAnchor?.current) {
            const height = navigationBarAnchor.current.offsetHeight;
            setMobileNavigationHeight(height);
        }
    };

    const handleContactClick = () => {
        setContactMenuOpen(!contactMenuOpen);
    };

    const scrollTo = (position: number = 0) => {
        window.scrollTo({ top: position, behavior: 'smooth' });
    };

    const handleAnchorClickMobileNavigation = (ref: RefObject<HTMLElement | null>, offset: number = 0) => {
        const position = ref.current!.offsetTop + offset;
        if (contactMenuOpen) {
            setTimeout(() => {
                scrollTo(position);
            }, 150);
        } else {
            scrollTo(position);
        }

        setMobileNavigationOpen(false);
        setContactMenuOpen(false);
    };

    const handleAnchorClick = (ref: RefObject<HTMLElement | null>, offset: number = 0) => {
        const position = ref.current!.offsetTop + offset;
        if (contactMenuOpen) {
            setTimeout(() => {
                scrollTo(position);
            }, 150);
        } else {
            scrollTo(position);
        }
        setContactMenuOpen(false);
    };

    const toggleContactMenuMobile = () => {
        setTimeout(() => {
            setMobileNavigationOpen(false);
            if (!contactMenuOpen) {
                setTimeout(() => {
                    setContactMenuOpen(true);
                }, 150);
            }
        }, 10);
    }

    useEffect(() => {
        if (navigationBarAnchor?.current) {
            const height = navigationBarAnchor.current.offsetHeight;
            setMobileNavigationHeight(height)
        }
    }, []);

    useEventListener('resize', () => {
        if (isDesktop) {
            setMobileNavigationOpen(false);
        }

        if (navigationBarAnchor?.current) {
            const height = navigationBarAnchor.current.offsetHeight;
            setMobileNavigationHeight(height)
        }
    });

    const socialLinks = SOCIAL_LINKS.map((item, index) => {
        const delay = [
            'delay-200',
            'delay-200',
            'delay-150',
            'delay-100',
            'delay-50',
        ][Math.clamp(index, 0, 3)];

        return {
            delay,
            ...item,
        };
    });

    return <div className="fixed w-dvw top-0 z-50 bg-light md:pt-2">
        {mobileNavigationOpen && <LockBodyDummy />}
        <div className={mc("relative bg-brand transition-all")}
            style={{
                top: mobileNavigationOpen ? `${setMobileNavigationHeight}px` : '0px',
                height: mobileNavigationOpen ? (isDesktop ? `calc(100dvh - 70px)` : `calc(100dvh - 46px)`) : '0px',
            }}
        >
            <div className="relative md:hidden h-[48px] flex justify-end items-center p-2">
                <button onClick={handleMobileMenuClick} className={mc("group opacity-0 -translate-x-[-100%] text-light rounded-lg transition-all cursor-pointer z-50 p-2" , {
                    'translate-x-0 opacity-100': mobileNavigationOpen,
                })}>
                    <FontAwesomeIcon icon={faXmark} size="xl" className="text-light group-hover:text-brand-light transition-all" />
                </button>
            </div>
            <div className={mc("flex flex-col justify-start px-8 gap-4 -translate-x-full", { 'translate-x-0': mobileNavigationOpen })}>
                {NAVIGATION_ITEMS.map(({ text, id, ref, offset, delay }) =>
                    <a
                        key={`${route} + ${text}`}
                        href={`#${id}`}
                        className={mc("opacity-0 -translate-x-full relative flex uppercase group font-medium text-2xl text-font-muted hover:text-font cursor-pointer overflow-hidden transition-all duration-250 ease-in-out", {
                            [`opacity-100 translate-x-0 ${delay}`]: mobileNavigationOpen
                        })}
                        onClick={event => { event.preventDefault(); handleAnchorClickMobileNavigation(ref, offset); }}
                    >
                        <div className="relative self-center overflow-hidden">
                            <span className="block h-min w-full group-hover:-translate-y-full text-font transition-all ease-in-out duration-250 z-50">{text}</span>
                            <span className="absolute top-0 h-min w-full translate-y-full group-hover:translate-none text-font transition-all ease-in-out duration-250 z-50">{text}</span>
                        </div>
                        <div className="absolute top-0 w-full h-full translate-y-full group-hover:translate-none transition-transform ease-in-out duration-500"></div>
                    </a>
                )}
                {!isDesktop &&
                    <a
                        className={mc("opacity-0 -translate-x-full relative flex uppercase group font-medium text-2xl text-font-muted hover:text-font cursor-pointer overflow-hidden transition-all duration-250 ease-in-out", {
                            [`opacity-100 translate-x-0 delay-200`]: mobileNavigationOpen
                        })}
                        onClick={event => { event.preventDefault(); toggleContactMenuMobile(); }}
                    >
                        <div className="relative self-center overflow-hidden">
                            <span className="block h-min w-full group-hover:-translate-y-full text-font transition-all ease-in-out duration-250 z-50">Contact</span>
                            <span className="absolute top-0 h-min w-full translate-y-full group-hover:translate-none text-font transition-all ease-in-out duration-250 z-50">Contact</span>
                        </div>
                        <div className="absolute top-0 w-full h-full translate-y-full group-hover:translate-none transition-transform ease-in-out duration-500"></div>
                    </a>
                }
            </div>
            <div className={mc("absolute bottom-0 opacity-0 w-full min-h-[72px] transition-all mb-8 px-8 flex flex-col justify-end items-end duration-500", {
                'opacity-100': mobileNavigationOpen
            })}>
                <div className="flex justify-end items-center gap-4 overflow-hidden">
                    {socialLinks.map(({ icon, scale, delay, href }) => <a href={href} target="_blank" rel="noindex,nofollow" className={mc("text-light -translate-y-[-100%] hover:text-brand-light opacity-0 transition-all duration-500 ease-in-out cursor-pointer", {
                        [`translate-y-0 opacity-100 ${delay}`]: mobileNavigationOpen,
                        [scale ?? '']: true,
                    })}>
                        <FontAwesomeIcon icon={icon} size="2x" className="text-light [&:focus,&:hover]:text-brand-light transition-all ease-in-out cursor-pointer" />
                    </a>)}
                </div>
                <h3 dangerouslySetInnerHTML={{ __html: CONTACT_DETAILS.companyLegalName }}
                    className={mc("text-font opacity-0 -translate-y-[-200%] text-xs pt-4 transition-all duration-500 ease-in-out overflow-hidden", {
                    'translate-y-0 opacity-100': mobileNavigationOpen
                })}></h3>
            </div>
        </div>
        <header className="relative md:flex justify-between items-center border-b-2 border-brand h-[48px] md:h-[72px] md:p-0">
            <div className={mc("md:hidden absolute top-0 flex justify-between items-center w-full h-full px-2 transition-all translate-x-none duration-250", {
                'translate-x-full': mobileNavigationOpen,
            })}>
                <div className="md:hidden"></div>
                <button onClick={handleMobileMenuClick} className="md:hidden p-2 hover:cursor-pointer hover:bg-brand-light rounded-lg transition-all z-100">
                    <FontAwesomeIcon icon={faBars} size="xl" className="text-brand" />
                </button>
            </div>
            <div ref={navigationBarAnchor as RefObject<HTMLDivElement | null>} className="w-full md:w-auto h-full absolute md:relative top-0 px-2 lg:px-10 flex justify-center md:justify-between items-center md:border-r-2 border-brand z-50">
                <a href="#landing"
                    onClick={event => {event.preventDefault(); isDesktop ? handleAnchorClick(homepageAnchors.home, -((navigationBarAnchor?.current?.offsetHeight ?? 0) + 20)) : handleAnchorClickMobileNavigation(homepageAnchors.home, -((navigationBarAnchor?.current?.offsetHeight ?? 0) + 2)); }}
                    className="group relative font-title tracking-wide text-2xl lg:text-4xl text-center cursor-pointer overflow-hidden"
                >
                    <div className="flex justify-center items-center group-hover:-translate-y-full transition-all ease-in-out duration-500 uppercase font-bold">
                        <span>Our</span>
                        <div className="w-[2px] h-8 mx-2 bg-brand"></div>
                        <span>Share</span>
                    </div>
                    <div className="absolute top-0 translate-y-full group-hover:translate-none flex justify-center items-center transition-all ease-in-out duration-500 uppercase font-bold">
                        <span>Our</span>
                        <div className="w-[2px] h-8 mx-2 bg-brand"></div>
                        <span>Share</span>
                    </div>
                </a>
            </div>
            <div className="w-full h-full hidden md:flex justify-around items-center">
                {NAVIGATION_ITEMS.map(({ text, id, ref, offset }) =>
                    <a
                        key={`${route} + ${text}`}
                        href={`#${id}`}
                        className="relative flex h-full uppercase group font-medium text-xl lg:text-2xl text-font-muted hover:text-font cursor-pointer overflow-hidden"
                        onClick={event => { event.preventDefault(); handleAnchorClick(ref, offset); }}
                    >
                        <div className="relative self-center overflow-hidden">
                            <span className="block px-2 lg:px-4 h-min w-full group-hover:-translate-y-full text-brand transition-all ease-in-out duration-250 z-50">{text}</span>
                            <span className="absolute top-0 px-2 lg:px-4 h-min w-full translate-y-full group-hover:translate-none text-font transition-all ease-in-out duration-250 z-50">{text}</span>
                        </div>
                        <div className="absolute top-0 w-full h-full bg-brand translate-y-full group-hover:translate-none transition-transform ease-in-out duration-500"></div>
                    </a>
                )}
            </div>
            <div className="h-full hidden md:flex justify-center items-center px-6 lg:px-10 text-xl border-l-2 transition-all ease-in-out duration-500 border-brand z-10">
                <button onClick={handleContactClick}
                    className={mc("relative group rounded-full px-4 py-1 lg:px-6 lg:py-2 hover:bg-brand uppercase font-medium border-2 border-brand cursor-pointer overflow-hidden transition-all ease-in-out duration-500 z-10", {
                        'bg-brand': contactMenuOpen
                    })}
                >
                    <div className="relative overflow-hidden text-lg lg:text-xl">
                        <span className={mc("block h-min group-hover:-translate-y-full transition-all ease-in-out duration-250", {
                            '-translate-y-full': contactMenuOpen
                        })}>Contact</span>
                        <span className={mc("absolute top-0 left-0 h-min text-font translate-y-full group-hover:translate-none transition-all ease-in-out duration-250", {
                            'translate-none': contactMenuOpen
                        })}>Contact</span>
                    </div>
                </button>
            </div>
        </header>
    </div>
}
